import apiService from '../services/api';
import { downloadBlob } from './downloadBlob';
import { isNativePlatform } from './platform';

/**
 * Types a browser shows without running anything. A blob: URL belongs to the
 * app's own origin, so an uploaded file the server labelled text/html or
 * image/svg+xml would run as a page of the app with the viewer's session
 * (security audit 2026-10). Anything else is saved, never shown.
 */
const VIEWABLE_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
  'text/csv',
  'text/plain',
]);

/** Is this blob safe to show in a tab of the app's origin? */
export function isViewableBlob(blob: Blob): boolean {
  return VIEWABLE_TYPES.has(blob.type.split(';')[0].trim().toLowerCase());
}

/**
 * Open a file served by an authenticated API route. A plain `<a href>` to such
 * a route is a browser navigation with no bearer token, and the server answers
 * "No token provided". So the file is fetched through the API client and shown
 * from a blob: URL in a new tab; in the app, where the tab is blocked, or when
 * the file is not a PDF, picture or plain text, it is saved instead.
 *
 * `path` is relative to the API base, as for any `apiService` call. Call it
 * straight from a click handler: the tab has to be opened before the fetch, or
 * the browser treats it as a pop-up.
 */
export async function openDocument(path: string, filename: string): Promise<void> {
  const tab = isNativePlatform() ? null : window.open('', '_blank');
  try {
    const blob = await (apiService.get(path, { responseType: 'blob', timeout: 60000 }) as unknown as Promise<Blob>);
    if (!tab || !isViewableBlob(blob)) {
      tab?.close();
      downloadBlob(new Blob([blob], { type: 'application/octet-stream' }), filename);
      return;
    }
    const url = URL.createObjectURL(blob);
    tab.location.href = url;
    // The tab has the file once it has loaded; the URL only needs to outlive that.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    tab?.close();
    throw err;
  }
}
